import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import type { Id } from '../../../../convex/_generated/dataModel';
import { api } from '../../../../convex/_generated/api';
import type { CommandInput } from '@drama/domain';
import type { HomeView, ProjectDetail } from '../../../../packages/domain/src/view';
import { record } from './analytics';
import { demo } from './demo';
export interface Service {
  promoteActor(actorId: string): Promise<unknown>;
  isDemo: boolean;
  home?: HomeView;
  detail?: ProjectDetail;
  members?: { email: string; role: string; active: boolean }[];
  create(a: { idea: string; genre: string; title?: string }): Promise<string>;
  request(a: CommandInput): Promise<unknown>;
  lockCast(id: string): Promise<unknown>;
  setDirection(id: string, direction: string): Promise<unknown>;
  castExisting(projectId: string, characterId: string, actorId: string): Promise<unknown>;
  acceptShot(id: string): Promise<unknown>;
  acceptEpisode(id: string, expectedMasterId?: string): Promise<unknown>;
  cancel(id: string): Promise<unknown>;
  url(id: string, download?: boolean): Promise<string>;
  review(a: {
    episodeId: string;
    scores: { identity: number; continuity: number; dialogue: number; story: number };
    wantsNext: boolean;
    notes: string;
  }): Promise<unknown>;
  seedConcepts(): Promise<unknown>;
  setMember(a: { email: string; role: string; active: boolean }): Promise<unknown>;
  reconcile(a: { jobId: string; costMicros: number; note: string }): Promise<unknown>;
}
const Context = createContext<Service | null>(null);
export const useLab = () => {
  const c = useContext(Context);
  if (!c) throw new Error('Lab provider is missing');
  return c;
};
export function DemoProvider({ projectId, children }: { projectId?: string; children: ReactNode }) {
  const state = useSyncExternalStore(demo.subscribe, demo.getSnapshot);
  return (
    <Context.Provider
      value={{
        ...demo,
        isDemo: true,
        home: state.home,
        detail: projectId ? state.details[projectId] : undefined,
        members: state.members,
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function LiveProvider({ projectId, children }: { projectId?: string; children: ReactNode }) {
  const home = useQuery(api.lab.home) as HomeView | undefined;
  const series = useQuery(
    api.lab.project,
    projectId ? { projectId: projectId as Id<'projects'> } : 'skip',
  ) as Omit<ProjectDetail, 'jobs'> | undefined;
  // Separate so a running job's heartbeat re-runs only the operation ledger, not the series.
  const jobs = useQuery(
    api.lab.projectJobs,
    projectId ? { projectId: projectId as Id<'projects'> } : 'skip',
  );
  const detail: ProjectDetail | undefined = series && { ...series, jobs: jobs ?? [] };
  const members = useQuery(api.lab.members, home?.access.role === 'owner' ? {} : 'skip');
  const promote = useMutation(api.lab.promoteActor);
  const create = useMutation(api.lab.create),
    request = useMutation(api.lab.request),
    lock = useMutation(api.lab.lockCast),
    direction = useMutation(api.lab.setDirection),
    cast = useMutation(api.lab.castExisting),
    shot = useMutation(api.lab.acceptShot),
    episode = useMutation(api.lab.acceptEpisode),
    cancel = useMutation(api.production.cancel),
    review = useMutation(api.lab.review),
    seed = useMutation(api.lab.seedConcepts),
    setMember = useMutation(api.lab.setMember),
    reconcile = useMutation(api.production.reconcile),
    url = useAction(api.storage.url);
  return (
    <Context.Provider
      value={{
        isDemo: false,
        home,
        detail,
        members,
        promoteActor: (actorId) => promote({ actorId: actorId as Id<'actors'> }),
        create: async (a) => {
          const id = await create(a);
          void record('concept_created');
          return id;
        },
        request: async (input) => {
          const result = await request({ input });
          void record('production_requested', { kind: input.kind });
          return result;
        },
        lockCast: async (projectId) => {
          const result = await lock({ projectId: projectId as Id<'projects'> });
          void record('cast_locked');
          return result;
        },
        setDirection: (projectId, value) =>
          direction({ projectId: projectId as Id<'projects'>, direction: value }),
        castExisting: (projectId, characterId, actorId) =>
          cast({
            projectId: projectId as Id<'projects'>,
            characterId: characterId as Id<'characters'>,
            actorId: actorId as Id<'actors'>,
          }),
        acceptShot: (shotId) => shot({ shotId: shotId as Id<'shots'> }),
        acceptEpisode: async (episodeId, expectedMasterId) => {
          const result = await episode({
            episodeId: episodeId as Id<'episodes'>,
            includeCurrentShots: !!expectedMasterId,
            expectedMasterId: expectedMasterId as Id<'assets'> | undefined,
          });
          void record('episode_accepted');
          return result;
        },
        cancel: (commandId) => cancel({ commandId: commandId as Id<'commands'> }),
        url: (assetId, download) => url({ assetId: assetId as Id<'assets'>, download }),
        review: (a) => review({ ...a, episodeId: a.episodeId as Id<'episodes'> }),
        seedConcepts: () => seed({}),
        setMember: (a) => setMember({ ...a, role: a.role as 'owner' | 'editor' | 'reviewer' }),
        reconcile: (a) => reconcile({ ...a, jobId: a.jobId as Id<'jobs'> }),
      }}
    >
      {children}
    </Context.Provider>
  );
}
